import { useEffect } from "react";
import { API_BASE } from "@lib/apiClient";

/**
 * Live event stream: a WebSocket to the user's RealtimeHub Durable Object.
 * Incoming events are re-broadcast as the same `aria:push` window event the
 * service-worker path uses, so every data hook that already refreshes on push
 * (messages, calls) now refreshes instantly — no per-hook changes needed.
 * The 20s polling stays as the fallback when the socket is down.
 */
export function useRealtime(enabled: boolean): void {
  useEffect(() => {
    if (!enabled || typeof WebSocket === "undefined") return;

    let ws: WebSocket | null = null;
    let closed = false;
    let attempts = 0;
    let reconnectTimer: number | undefined;
    let pingTimer: number | undefined;

    const wsUrl = (): string | null => {
      const token = localStorage.getItem("aria.token");
      if (!token) return null;
      // API_BASE is absolute in production builds (https://…/api); a relative
      // /api resolves against the page origin for local dev.
      const u = new URL(`${API_BASE}/realtime/ws`, window.location.origin);
      u.protocol = u.protocol === "http:" ? "ws:" : "wss:";
      u.searchParams.set("token", token);
      return u.toString();
    };

    const connect = (): void => {
      if (closed) return;
      const url = wsUrl();
      if (!url) return;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempts = 0;
        // Keep intermediaries from idling the socket out.
        pingTimer = window.setInterval(() => {
          try {
            ws?.send("ping");
          } catch {
            /* reconnect handles it */
          }
        }, 45_000);
      };
      ws.onmessage = (ev) => {
        if (typeof ev.data !== "string" || ev.data === "pong") return;
        try {
          const msg = JSON.parse(ev.data) as { type?: string; eventType?: string };
          if (msg.type === "event" && msg.eventType) {
            window.dispatchEvent(new CustomEvent("aria:push", { detail: { eventType: msg.eventType } }));
          }
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onclose = () => {
        window.clearInterval(pingTimer);
        scheduleReconnect();
      };
      ws.onerror = () => {
        try {
          ws?.close();
        } catch {
          /* onclose reconnects */
        }
      };
    };

    const scheduleReconnect = (): void => {
      if (closed) return;
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(attempts, 5));
      attempts++;
      reconnectTimer = window.setTimeout(connect, delay);
    };

    connect();
    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      window.clearInterval(pingTimer);
      try {
        ws?.close();
      } catch {
        /* done */
      }
    };
  }, [enabled]);
}
